// src/components/home/StoryViewer.jsx
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { db } from '../../firebase';
import { doc, updateDoc, arrayUnion, increment } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { formatRelativeTime } from '../../utils/feedHelpers';

const STORY_DURATION_MS = 5000;
const SWIPE_THRESHOLD_X = 80;
const SWIPE_THRESHOLD_Y = 100;
const TAP_HOLD_MS = 200;

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const getStoryItems = (story) => {
  if (!story) return [];
  if (Array.isArray(story.items) && story.items.length) {
    return story.items.map((i) => ({ url: i.url, type: i.type || 'image' }));
  }
  if (Array.isArray(story.photos) && story.photos.length) {
    return story.photos.map((url) => ({ url, type: 'image' }));
  }
  return [];
};

const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/* ================================================================== */
/* StoryViewer                                                         */
/* ================================================================== */

const StoryViewer = ({ stories = [], initialIndex = 0, onClose, onEdit }) => {
  const { user, isAdmin } = useAuth();

  const [storyIndex, setStoryIndex] = useState(initialIndex);
  const [itemIndex, setItemIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
  const [navDirection, setNavDirection] = useState(null); // 'next' | 'prev'

  const rafRef = useRef(null);
  const startTimeRef = useRef(0);
  const elapsedRef = useRef(0);
  const videoRef = useRef(null);
  const markedViewsRef = useRef(new Set());

  // Swipe tracking refs
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchDeltaRef = useRef({ x: 0, y: 0 });
  const swipingRef = useRef(false);
  const rootRef = useRef(null);

  /* -------------------- Derived -------------------- */
  const story = stories[storyIndex];
  const items = useMemo(() => getStoryItems(story), [story]);
  const current = items[itemIndex];
  const isVideo = current?.type === 'video';

  const canManage = !!user && (user.uid === story?.authorId || isAdmin);

  const handlersRef = useRef({ goNext: null, goPrev: null, onClose: null });
  handlersRef.current.onClose = onClose;

  /* -------------------- Watch reduced motion -------------------- */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setReduceMotion(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  /* -------------------- Reset media loaded -------------------- */
  useEffect(() => {
    setMediaLoaded(false);
  }, [storyIndex, itemIndex]);

  /* -------------------- Mark viewed -------------------- */
  useEffect(() => {
    if (!user?.uid || !story?.id) return;
    if (markedViewsRef.current.has(story.id)) return;
    if (story.views?.includes(user.uid)) {
      markedViewsRef.current.add(story.id);
      return;
    }
    markedViewsRef.current.add(story.id);
    updateDoc(doc(db, 'stories', story.id), {
      views: arrayUnion(user.uid),
      viewCount: increment(1),
    }).catch((err) => console.warn('view mark failed', err));
  }, [user?.uid, story?.id, story?.views]);

  /* -------------------- Preload next -------------------- */
  useEffect(() => {
    if (!story || !items.length) return;
    const nextIdx = itemIndex + 1 < items.length ? itemIndex + 1 : -1;
    if (nextIdx >= 0 && items[nextIdx]?.type === 'image') {
      const img = new Image();
      img.src = items[nextIdx].url;
    }
    if (itemIndex === 0 && storyIndex + 1 < stories.length) {
      const nextStoryItems = getStoryItems(stories[storyIndex + 1]);
      if (nextStoryItems[0]?.type === 'image') {
        const img = new Image();
        img.src = nextStoryItems[0].url;
      }
    }
  }, [story, items, itemIndex, storyIndex, stories]);

  /* -------------------- Navigation -------------------- */
  const goNext = useCallback(() => {
    elapsedRef.current = 0;
    setProgress(0);
    setNavDirection('next');
    if (itemIndex < items.length - 1) {
      setItemIndex((i) => i + 1);
    } else if (storyIndex < stories.length - 1) {
      setStoryIndex((i) => i + 1);
      setItemIndex(0);
    } else {
      onClose?.();
    }
  }, [itemIndex, items.length, storyIndex, stories.length, onClose]);

  const goPrev = useCallback(() => {
    elapsedRef.current = 0;
    setProgress(0);
    setNavDirection('prev');
    if (itemIndex > 0) {
      setItemIndex((i) => i - 1);
    } else if (storyIndex > 0) {
      const prevItems = getStoryItems(stories[storyIndex - 1]);
      setStoryIndex((i) => i - 1);
      setItemIndex(Math.max(0, prevItems.length - 1));
    }
  }, [itemIndex, storyIndex, stories]);

  handlersRef.current.goNext = goNext;
  handlersRef.current.goPrev = goPrev;

  /* -------------------- Progress timer (images) -------------------- */
  useEffect(() => {
    if (!story || !current) return;
    if (paused || menuOpen || swipingRef.current) return;
    if (isVideo) return;

    startTimeRef.current = Date.now() - elapsedRef.current;
    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min(1, elapsed / STORY_DURATION_MS);
      setProgress(pct);
      if (pct >= 1) {
        handlersRef.current.goNext?.();
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [story, current, itemIndex, storyIndex, paused, menuOpen, isVideo]);

  useEffect(() => {
    elapsedRef.current = 0;
    setProgress(0);
  }, [storyIndex, itemIndex]);

  /* -------------------- Video autoplay + progress -------------------- */
  useEffect(() => {
    const v = videoRef.current;
    if (!isVideo || !v) return;

    try {
      v.currentTime = 0;
    } catch {
      /* ignore */
    }
    v.muted = !unmuted;
    if (!paused && !menuOpen) {
      v.play().catch(() => {});
    }

    const onTime = () => {
      if (!v.duration || !Number.isFinite(v.duration)) return;
      setProgress(Math.min(1, v.currentTime / v.duration));
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [current, paused, menuOpen, isVideo, unmuted]);

  useEffect(() => {
    const v = videoRef.current;
    if (!isVideo || !v) return;
    if (paused || menuOpen) {
      v.pause();
    } else {
      v.play().catch(() => {});
    }
  }, [paused, menuOpen, current, isVideo]);

  /* -------------------- Keyboard -------------------- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (menuOpen) setMenuOpen(false);
        else handlersRef.current.onClose?.();
        return;
      }
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handlersRef.current.goNext?.();
      }
      if (e.key === 'ArrowLeft') handlersRef.current.goPrev?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  /* -------------------- Close menu on outside click -------------------- */
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', close);
    };
  }, [menuOpen]);

  /* -------------------- Body scroll lock -------------------- */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* -------------------- Touch / swipe gestures -------------------- */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      if (menuOpen) return;
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
      touchDeltaRef.current = { x: 0, y: 0 };
      swipingRef.current = false;
    };

    const onTouchMove = (e) => {
      if (menuOpen) return;
      const t = e.touches[0];
      const dx = t.clientX - touchStartRef.current.x;
      const dy = t.clientY - touchStartRef.current.y;
      touchDeltaRef.current = { x: dx, y: dy };

      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        swipingRef.current = true;
        setPaused(true);
        el.style.transform = `translateX(${dx * 0.4}px)`;
      } else if (dy > 60 && Math.abs(dy) > Math.abs(dx) * 1.2) {
        swipingRef.current = true;
        setPaused(true);
        el.style.transform = `translateY(${dy * 0.5}px)`;
        el.style.opacity = String(Math.max(0.3, 1 - dy / 500));
      }
    };

    const onTouchEnd = () => {
      if (!swipingRef.current) return;
      const { x: dx, y: dy } = touchDeltaRef.current;
      const elapsed = Date.now() - touchStartRef.current.time;

      el.style.transform = '';
      el.style.opacity = '';
      swipingRef.current = false;
      setPaused(false);

      if (Math.abs(dx) > SWIPE_THRESHOLD_X && Math.abs(dx) > Math.abs(dy) * 1.2) {
        if (dx < 0) handlersRef.current.goNext?.();
        else handlersRef.current.goPrev?.();
      } else if (dy > SWIPE_THRESHOLD_Y && elapsed < 500) {
        handlersRef.current.onClose?.();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [menuOpen]);

  /* -------------------- Early exit -------------------- */
  if (!story || !current) return null;

  /* -------------------- Media animation keyframes -------------------- */
  const mediaAnimClass = reduceMotion
    ? ''
    : navDirection === 'next'
    ? 'animate-[slideInRight_200ms_ease-out]'
    : navDirection === 'prev'
    ? 'animate-[slideInLeft_200ms_ease-out]'
    : '';

  /* -------------------- Render -------------------- */
  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[90] bg-black flex items-center justify-center transition-transform duration-200 ease-out select-none"
      role="dialog"
      aria-modal="true"
      aria-label={`Story by ${story.authorName || 'user'}`}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Ambient blurred backdrop */}
      {current.type === 'image' && (
        <div
          className="absolute inset-0 opacity-40 blur-3xl scale-110"
          style={{
            backgroundImage: `url(${current.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />
      )}

      {/* Tap surfaces — full height on left/right halves */}
      <button
        type="button"
        aria-label="Previous story"
        onClick={(e) => {
          if (menuOpen) return;
          e.stopPropagation();
          handlersRef.current.goPrev?.();
        }}
        onPointerDown={(e) => {
          if (e.clientY > 100 && !menuOpen) setPaused(true);
        }}
        onPointerUp={() => setPaused(false)}
        onPointerLeave={() => setPaused(false)}
        className="absolute left-0 top-24 bottom-24 w-1/2 z-10 focus:outline-none"
      />

      <button
        type="button"
        aria-label="Next story"
        onClick={(e) => {
          if (menuOpen) return;
          e.stopPropagation();
          handlersRef.current.goNext?.();
        }}
        onPointerDown={(e) => {
          if (e.clientY > 100 && !menuOpen) setPaused(true);
        }}
        onPointerUp={() => setPaused(false)}
        onPointerLeave={() => setPaused(false)}
        className="absolute right-0 top-24 bottom-24 w-1/2 z-10 focus:outline-none"
      />

      {/* Pause indicator */}
      {paused && !menuOpen && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none opacity-60">
          <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <i className="fas fa-pause text-white text-2xl" aria-hidden="true" />
          </div>
        </div>
      )}

      {/* Progress bars */}
      <div
        className="absolute left-3 right-3 flex gap-1 z-20"
        style={{ top: 'calc(0.75rem + env(safe-area-inset-top))' }}
      >
        {items.map((_, i) => (
          <div
            key={i}
            className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden shadow-sm"
          >
            <div
              className="h-full bg-white transition-[width] duration-100 ease-linear"
              style={{
                width:
                  i < itemIndex
                    ? '100%'
                    : i === itemIndex
                    ? `${progress * 100}%`
                    : '0%',
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div
        className="absolute left-3 right-3 flex items-center gap-3 z-30 text-white"
        style={{ top: 'calc(2rem + env(safe-area-inset-top))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-700 flex-shrink-0 ring-2 ring-white/20">
          {story.authorPhoto ? (
            <img
              src={story.authorPhoto}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-bold">
              {story.authorName?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate drop-shadow">
            {story.authorName || 'User'}
          </p>
          <p className="text-xs text-white/70">
            {formatRelativeTime(story.createdAt) || 'just now'}
          </p>
        </div>

        {/* Unmute toggle for videos */}
        {isVideo && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setUnmuted((u) => !u);
            }}
            aria-label={unmuted ? 'Mute video' : 'Unmute video'}
            className="w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition active:scale-90 backdrop-blur-sm"
          >
            <i
              className={`fas ${
                unmuted ? 'fa-volume-up' : 'fa-volume-mute'
              } text-sm`}
              aria-hidden="true"
            />
          </button>
        )}

        {canManage && onEdit && (
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              aria-label="Story options"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition active:scale-90 backdrop-blur-sm"
            >
              <i className="fas fa-ellipsis-h text-sm" aria-hidden="true" />
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden z-40 border border-gray-200 dark:border-gray-700"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(story);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 active:bg-gray-200 dark:active:bg-gray-600"
                >
                  <i
                    className="fas fa-pen text-orange text-xs"
                    aria-hidden="true"
                  />
                  Edit Story
                </button>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          aria-label="Close"
          className="w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition active:scale-90 backdrop-blur-sm"
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      </div>

      {/* Media */}
      <div
        className={`relative max-h-full max-w-full flex items-center justify-center z-10 ${mediaAnimClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!mediaLoaded && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <i
              className="fas fa-spinner fa-spin text-white/50 text-2xl"
              aria-hidden="true"
            />
          </div>
        )}
        {isVideo ? (
          <video
            ref={videoRef}
            src={current.url}
            className="max-h-full max-w-full object-contain select-none"
            playsInline
            muted={!unmuted}
            controls={false}
            onLoadedData={() => setMediaLoaded(true)}
            onEnded={() => handlersRef.current.goNext?.()}
            onError={() => handlersRef.current.goNext?.()}
          />
        ) : (
          <img
            src={current.url}
            alt=""
            className="max-h-full max-w-full object-contain select-none"
            draggable={false}
            onLoad={() => setMediaLoaded(true)}
          />
        )}
      </div>

      {/* Caption */}
      {story.caption && (
        <div
          className="absolute left-0 right-0 px-6 text-center z-20 pointer-events-none"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom))' }}
        >
          <p className="inline-block bg-black/60 backdrop-blur-sm text-white text-sm rounded-xl px-4 py-2 max-w-md leading-relaxed">
            {story.caption}
          </p>
        </div>
      )}

      {/* Counter */}
      {items.length > 1 && (
        <div
          className="absolute left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 backdrop-blur-sm rounded-full px-3 py-1 z-20"
          style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
        >
          {itemIndex + 1} / {items.length}
        </div>
      )}

      {/* Desktop nav */}
      {stories.length > 1 && (
        <>
          {storyIndex > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setStoryIndex((i) => i - 1);
                setItemIndex(0);
              }}
              aria-label="Previous story"
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white items-center justify-center z-30 transition active:scale-90"
            >
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
          )}
          {storyIndex < stories.length - 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setStoryIndex((i) => i + 1);
                setItemIndex(0);
              }}
              aria-label="Next story"
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white items-center justify-center z-30 transition active:scale-90"
            >
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default StoryViewer;